import { Injectable } from '@angular/core';
import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { TranslateService } from '@ngx-translate/core';
import { FileItem } from 'ng2-file-upload';
import { Observable, from, iif, map, of, switchMap } from 'rxjs';
import { FileUploadConfirmDialogComponent } from 'src/app/shared/components/file-upload-confirm-dialog/file-upload-confirm-dialog.component';
import { FileSizePipe } from 'src/app/shared/pipes/file-size/file-size.pipe';
import * as FileUtils from '../../../../utils/file/file-utils';
import { ErrorNotificationMode, FileModel, FileUploadValidationErrors, UploadFileItem, ValidAndInvalidFiles } from '../../models';
import { UserNotificationService } from '../user-notification.service';

interface FileInfo {
    name: string;
    type: string;
    sizeInBytes: number;
}
@Injectable({
    providedIn: 'root',
})
export class FilesValidatorService {
    constructor(
        private fileSizePipe: FileSizePipe,
        private modalService: NgbModal,
        private translateService: TranslateService,
        private userNotificationService: UserNotificationService,
    ) {}

    getValidAndInvalidFiles$<T extends UploadFileItem | FileModel>(
        filesToBeAdded: T[], // filesToBeAdded:coming from uploading or selecting and need to be divided into valid and invalid files
        selectedFiles: FileModel[] | undefined, // these files we need for checking for already existing files
        filesCurrentlyUploading: FileItem[], // these files we need for checking for already existing files
        validationOptions: {
            errorNotificationMode: ErrorNotificationMode;
            isFileTypeSupportedOptions?: { supportedFileTypes: Array<string> | undefined; errorMessageKey?: string };
            isMaxFileSizeExceededOptions?: { maxInputSizeInBytes: number; errorMessageKey?: string; showGeneralMaxInputSizeMessage?: boolean };
            checkFileNameOptions?: { allowSameFileNames?: boolean; checkSingleFileNameForInvalidCharacters?: boolean };
        },
    ): Observable<ValidAndInvalidFiles<T>> {
        const allFiles: Array<FileModel | FileItem> = [...(selectedFiles || []), ...filesCurrentlyUploading];

        const validAndInvalidFiles: ValidAndInvalidFiles<T> = { validFiles: [], invalidFiles: [] };

        const validationErrors: FileUploadValidationErrors = {
            special_characters: [],
            file_type: [],
            max_file_size: [],
            same_name_exists: [],
            same_name_exists_more_than_once: [],
        };

        const seenNames = new Set<string>();
        filesToBeAdded = filesToBeAdded.filter(file => {
            const fileInfo = this.getFileInfo<T>(file);

            const fileNameWithoutExtension = fileInfo.name.substring(0, fileInfo.name.lastIndexOf('.')).trim().toLowerCase();

            if (seenNames.has(fileNameWithoutExtension)) {
                return false;
            }

            seenNames.add(fileNameWithoutExtension);
            return true;
        });
        filesToBeAdded.forEach((file: T) => {
            this.userNotificationService.hideSpinner();
            let fileHasError = false;

            const fileInfo = this.getFileInfo<T>(file);

            if (validationOptions.isFileTypeSupportedOptions) {
                const fileTypeNotSupportedErrorMessage = this.getErrorMessageIfFiletypeIsNotSupported(
                    fileInfo,
                    validationOptions.isFileTypeSupportedOptions.supportedFileTypes,
                    validationOptions.isFileTypeSupportedOptions.errorMessageKey,
                );
                if (fileTypeNotSupportedErrorMessage) {
                    file['unsupportedFileTypeError'] = true;
                    fileHasError = true;
                    validationErrors['file_type'].push(fileInfo.name);
                    if (validationOptions.errorNotificationMode === 'notification') {
                        this.userNotificationService.showError(fileTypeNotSupportedErrorMessage);
                    }
                }
            }

            if (validationOptions.isMaxFileSizeExceededOptions) {
                const maxFileSizeExceededErrorMessage = this.getErrorMessageIfMaxFileSizeExceeded(
                    fileInfo,
                    validationOptions.isMaxFileSizeExceededOptions.maxInputSizeInBytes,
                    validationOptions.isMaxFileSizeExceededOptions.errorMessageKey,
                    validationOptions.isMaxFileSizeExceededOptions.showGeneralMaxInputSizeMessage,
                );
                if (maxFileSizeExceededErrorMessage) {
                    file['maxFileSizeError'] = true;
                    fileHasError = true;
                    validationErrors['max_file_size'].push(fileInfo.name);
                    if (validationOptions.errorNotificationMode === 'notification') {
                        this.userNotificationService.showError(maxFileSizeExceededErrorMessage);
                    }
                }
            }

            if (validationOptions.checkFileNameOptions) {
                if (validationOptions.checkFileNameOptions.checkSingleFileNameForInvalidCharacters) {
                    const fileNameNotSupportedErrorMessage = this.getErrorMessageIfFileNameContainsInvalidCharacters(fileInfo);
                    if (fileNameNotSupportedErrorMessage) {
                        file['unsupportedFileName'] = true;
                        fileHasError = true;
                        validationErrors['special_characters'].push(fileInfo.name);
                        if (validationOptions.errorNotificationMode === 'notification') {
                            this.userNotificationService.showError(fileNameNotSupportedErrorMessage);
                        }
                    }
                }

                if (!validationOptions.checkFileNameOptions.allowSameFileNames) {
                    const fileNameExistsMoreThanOnceError = this.getErrorMessageIfFileNameContainedMoreThanOnceInSelectedFileNames(fileInfo, allFiles);
                    if (fileNameExistsMoreThanOnceError) {
                        file['sameNameExistsMoreThanOnceError'] = true;
                        fileHasError = true;
                        validationErrors['same_name_exists_more_than_once'].push(fileInfo.name);
                        if (validationOptions.errorNotificationMode === 'notification') {
                            this.userNotificationService.showError(fileNameExistsMoreThanOnceError);
                        }
                    } else {
                        // only show fileNameExistsError when there is only one file of it in the allFiles array
                        const fileNameExistsError = this.getErrorMessageIfFileNameExistsInFiles(fileInfo, allFiles);
                        if (fileNameExistsError) {
                            file['sameNameExistsError'] = true;
                            fileHasError = true;
                            validationErrors['same_name_exists'].push(fileInfo.name);
                            if (validationOptions.errorNotificationMode === 'notification') {
                                this.userNotificationService.showError(fileNameExistsError);
                            }
                        }
                    }
                }
            }

            if (fileHasError) {
                validAndInvalidFiles.invalidFiles.push(file);
            } else {
                validAndInvalidFiles.validFiles.push(file);
            }
        });

        const validationErrorsOrNull = Object.values(validationErrors).some((row: string[]) => row.length > 0) ? validationErrors : null;

        return iif(
            () => validationOptions.errorNotificationMode === 'confirmation' && !!validationErrorsOrNull,
            of(filesToBeAdded).pipe(
                switchMap(() => {
                    const modalRef: NgbModalRef = this.modalService.open(FileUploadConfirmDialogComponent);
                    modalRef.componentInstance.validationErrors = validationErrorsOrNull;
                    modalRef.componentInstance.hasSomeValidFiles = validAndInvalidFiles.validFiles.length > 0;
                    return from(modalRef.result).pipe(
                        map((confirm: boolean) => {
                            if (confirm) {
                                return validAndInvalidFiles;
                            }
                            throw new Error('cancel upload or selection');
                        }),
                    );
                }),
            ),
            of(validAndInvalidFiles),
        );
    }

    getCurrentlyUploadingFiles(filesInUploaderQueue: FileItem[], numberOfFilesToBeAdded: number): FileItem[] {
        if (filesInUploaderQueue.length === 0) {
            return [];
        }
        // uploader queue contains the already uploading files + the files to be added
        // so we have to remove the number of items from the back that we want to add
        const files = [...filesInUploaderQueue];
        files.splice(filesInUploaderQueue.length - numberOfFilesToBeAdded, numberOfFilesToBeAdded);
        return files;
    }

    private getFileInfo<T extends UploadFileItem | FileModel>(file: T): FileInfo {
        return {
            name: 'file' in file ? file.file.name || '' : file.name || '',
            type: 'file' in file ? file.file.type || '' : file.type || '',
            sizeInBytes: 'file' in file ? file.file.size : file.sizeInBytes,
        };
    }

    private getErrorMessageIfFileNameExistsInFiles(fileInfo: FileInfo, allFiles: Array<FileModel | FileItem>): string | undefined {
        const nameWithoutExtension = FileUtils.getFileNameWithoutExtension(fileInfo.name);
        const fileNamesOfAllFiles = allFiles.map(file => {
            return typeof (file as FileItem).file === 'undefined' ? (file as FileModel).name : (file as FileItem).file.name || '';
        });
        if (
            nameWithoutExtension &&
            FileUtils.isFileNameWithoutExtensionContainedInListOfFileNames(
                FileUtils.replaceFileNameInvalidCharacters(nameWithoutExtension),
                fileNamesOfAllFiles,
            )
        ) {
            return this.translateService.instant('VALIDATION.FILE_ALREADY_EXISTS', { name: fileInfo.name });
        }
        return undefined;
    }

    private getErrorMessageIfFileNameContainedMoreThanOnceInSelectedFileNames(fileInfo: FileInfo, allFiles: Array<FileModel | FileItem>): string | undefined {
        const fileName = FileUtils.getFileNameWithExtension(fileInfo.name);
        const fileNamesOfAllFiles = allFiles.map(file => {
            return typeof (file as FileItem).file === 'undefined' ? (file as FileModel).name : (file as FileItem).file.name || '';
        });

        if (
            fileName &&
            FileUtils.isFileNameWithExtensionContainedMoreThanOnceInFileNames(FileUtils.replaceFileNameInvalidCharacters(fileName), fileNamesOfAllFiles)
        ) {
            return this.translateService.instant('VALIDATION.MULTIPLE_FILES_WITH_THE_SAME_NAME_SELECTED', { name: fileInfo.name });
        }
        return undefined;
    }

    private getErrorMessageIfMaxFileSizeExceeded(
        fileInfo: FileInfo,
        maxInputSizeInBytes: number,
        errorMessageKey?: string,
        showGeneralMaxInputSizeMessage?: boolean,
    ): string | undefined {
        if (fileInfo.sizeInBytes > maxInputSizeInBytes) {
            const messageKey =
                (showGeneralMaxInputSizeMessage === false || showGeneralMaxInputSizeMessage == null) && errorMessageKey
                    ? errorMessageKey
                    : 'VALIDATION.EXCEEDED_FILE_SIZE';
            return this.translateService.instant(messageKey, {
                maxFileSize: this.fileSizePipe.transform(maxInputSizeInBytes, 0),
                fileName: fileInfo.name,
            });
        }
        return undefined;
    }

    private getErrorMessageIfFiletypeIsNotSupported(
        fileInfo: FileInfo,
        supportedFileTypes: Array<string> | undefined,
        errorMessageKey?: string,
    ): string | undefined {
        let reallySupportedFileTypes: string[] = [];
        if (supportedFileTypes && supportedFileTypes.length > 0) {
            reallySupportedFileTypes = supportedFileTypes.map((t: string) => (t.startsWith('.') ? t.substring(1, t.length) : t));
        } else {
            return undefined;
        }
        if (!FileUtils.isSupportedType(fileInfo.name, reallySupportedFileTypes)) {
            return errorMessageKey
                ? this.translateService.instant(errorMessageKey, { fileName: fileInfo.name })
                : this.translateService.instant('VALIDATION.TYPE_NOT_SUPPORTED', { fileName: fileInfo.name, fileType: fileInfo.type });
        }
    }

    private getErrorMessageIfFileNameContainsInvalidCharacters(fileInfo: FileInfo): string | undefined {
        if (FileUtils.isInvalidCharacterContainedInFilename(fileInfo.name)) {
            return this.translateService.instant('VALIDATION.FILE_CANNOT_BE_SELECTED_BECAUSE_IT_CONTAINS_INVALID_CHARACTER', {
                name: fileInfo.name,
            });
        }
        return undefined;
    }
}
